import { COMMAND_BAR_TRIGGER_ID } from "@memorang/ui/components/CustomAppbar/CommandbarContainer/constants";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import {
	type Action,
	KBAR_LISTBOX,
	KBarAnimator,
	type KBarOptions,
	KBarPortal,
	KBarPositioner,
	KBarProvider,
	VisualState,
	getListboxItemId,
	useKBar,
} from "kbar";
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import type { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import type {
	NativeSyntheticEvent,
	View as RNView,
	TextInputKeyPressEventData,
} from "react-native";
import { TouchableWithoutFeedback, View } from "react-native";
import { Surface, TextInput } from "react-native-paper";

import Results from "./Results";

type CommandBarProviderProps = PropsWithChildren<{
	initialActions?: Action[];
	options?: KBarOptions;
}>;

const blurCommandBarTriggerFocus = () => {
	if (
		typeof document === "undefined" ||
		typeof requestAnimationFrame === "undefined"
	) {
		return;
	}
	requestAnimationFrame(() => {
		const activeElement = document.activeElement;
		if (!(activeElement instanceof HTMLElement)) {
			return;
		}

		if (
			activeElement.id === COMMAND_BAR_TRIGGER_ID ||
			activeElement.closest(`#${COMMAND_BAR_TRIGGER_ID}`)
		) {
			activeElement.blur();
		}
	});
};

const closeCommandBar = (query: ReturnType<typeof useKBar>["query"]) => {
	query.setVisualState((visualState) => {
		if (
			visualState === VisualState.hidden ||
			visualState === VisualState.animatingOut
		) {
			return visualState;
		}
		return VisualState.animatingOut;
	});
	blurCommandBarTriggerFocus();
};

const getDomInput = (wrapper: RNView | null): HTMLInputElement | null => {
	if (typeof HTMLElement === "undefined" || !(wrapper instanceof HTMLElement)) {
		return null;
	}

	const input = wrapper.querySelector("input");
	return input instanceof HTMLInputElement ? input : null;
};

const KBarSearchInput = () => {
	const { t } = useTranslation();
	const theme = useAppTheme();
	const wrapperRef = useRef<RNView>(null);
	const { query, currentRootActionId, actions, activeIndex, showing } = useKBar(
		(state) => ({
			currentRootActionId: state.currentRootActionId,
			actions: state.actions,
			activeIndex: state.activeIndex,
			showing: state.visualState === VisualState.showing,
		}),
	);
	const [localSearch, setLocalSearch] = useState("");
	const localSearchRef = useRef("");

	useEffect(() => {
		const stopEscapeWhenClearingSearch = (event: KeyboardEvent) => {
			if (event.key === "Escape" && localSearchRef.current.length > 0) {
				event.stopPropagation();
			}
		};
		document.addEventListener("keydown", stopEscapeWhenClearingSearch);
		return () => {
			document.removeEventListener("keydown", stopEscapeWhenClearingSearch);
		};
	}, []);

	const syncKbarInputRef = useCallback(() => {
		const domInput = getDomInput(wrapperRef.current);
		if (domInput) {
			query.inputRefSetter(domInput);
		}
		return domInput;
	}, [query]);

	useLayoutEffect(() => {
		const domInput = syncKbarInputRef();
		if (!domInput) {
			return;
		}

		domInput.setAttribute("role", "combobox");
		domInput.setAttribute("aria-expanded", String(showing));
		domInput.setAttribute("aria-controls", KBAR_LISTBOX);
		domInput.setAttribute(
			"aria-activedescendant",
			getListboxItemId(activeIndex),
		);
	}, [activeIndex, showing, syncKbarInputRef]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset search when navigating action roots
	useEffect(() => {
		localSearchRef.current = "";
		setLocalSearch("");
		query.setSearch("");
		syncKbarInputRef()?.focus();
	}, [currentRootActionId, query, syncKbarInputRef]);

	const handleChange = (text: string) => {
		localSearchRef.current = text;
		setLocalSearch(text);
		query.setSearch(text);
	};

	const handleKeyPress = ({
		nativeEvent,
	}: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
		const { key: keyValue } = nativeEvent;

		if (keyValue === "Escape") {
			if (localSearch.length > 0) {
				handleChange("");
				return;
			}

			syncKbarInputRef()?.blur();
			closeCommandBar(query);
			return;
		}

		if (currentRootActionId && !localSearch && keyValue === "Backspace") {
			const parent = actions[currentRootActionId]?.parent;
			query.setCurrentRootAction(parent ?? null);
		}
	};

	return (
		<View ref={wrapperRef}>
			<TextInput
				mode="flat"
				placeholder={t("commandBar.input.placeholder")}
				value={localSearch}
				onChangeText={handleChange}
				onKeyPress={handleKeyPress}
				autoFocus
				autoComplete="off"
				spellCheck={false}
				underlineColor="transparent"
				activeUnderlineColor="transparent"
				placeholderTextColor={theme.colors.onSurfaceVariant}
				textColor={theme.colors.onSurface}
				style={{
					backgroundColor: "transparent",
				}}
			/>
		</View>
	);
};

export const CommandBarProvider = ({
	children,
	initialActions,
	options,
}: CommandBarProviderProps) => {
	const theme = useAppTheme();

	return (
		<KBarProvider
			actions={initialActions}
			options={{
				disableScrollbarManagement: true,
				enableHistory: true,
				...options,
				callbacks: {
					...options?.callbacks,
					onClose: () => {
						blurCommandBarTriggerFocus();
						options?.callbacks?.onClose?.();
					},
				},
			}}
		>
			<KBarPortal>
				<TouchableWithoutFeedback>
					<View
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							backgroundColor: theme.colors.backdrop,
							zIndex: 1300,
						}}
					>
						<KBarPositioner>
							<KBarAnimator style={{ width: "100%", maxWidth: "600px" }}>
								<Surface
									elevation={1}
									style={{
										width: "100%",
										backgroundColor: theme.colors.surface,
									}}
								>
									<KBarSearchInput />
									<Results />
								</Surface>
							</KBarAnimator>
						</KBarPositioner>
					</View>
				</TouchableWithoutFeedback>
			</KBarPortal>
			{children}
		</KBarProvider>
	);
};
